import { forwardRef } from 'react';
import { CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GoogleAdsAccount } from '@/lib/types';
import googleAds from '/google-ads.svg';

export interface AccountListItemProps {
  account: GoogleAdsAccount;
  selected?: boolean;
  onSelect?: (account: GoogleAdsAccount) => Promise<void>;
}

/**
 * One Google Ads account row — same anatomy as the Meta picker's rows
 * (meta-ad-account-selection-modal): platform logo tile, name over `id · currency`,
 * cobalt ring + green "Active" chip on the selected row. Radio buttons and initials
 * avatars were dropped for that parity (#5936).
 */
export const GoogleAdsAccountItem = forwardRef<HTMLDivElement, AccountListItemProps>(
  ({ account, selected, onSelect }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'w-full flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors bg-white',
          selected ? 'ring-1 ring-[#290BD9]' : 'hover:bg-gray-50'
        )}
        onClick={() => onSelect?.(account)}
        role='button'
        tabIndex={0}
        aria-selected={selected}
      >
        <div className='flex-shrink-0 w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center'>
          <img src={googleAds} alt='Google Ads' className='w-4 h-4' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-gray-900 truncate'>
            {account.name || account.googleads_account_id}
          </p>
          <p className='text-xs text-gray-500 truncate'>
            {account.googleads_account_id}
            {account.currency ? ` · ${account.currency}` : ''}
          </p>
        </div>
        {selected && (
          <div className='flex items-center gap-1 text-xs font-medium text-[#047857]'>
            <CheckCircle className='h-4 w-4' />
            Active
          </div>
        )}
      </div>
    );
  }
);

GoogleAdsAccountItem.displayName = 'GoogleAdsAccountItem';
